import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAdmins } from '../../store/slices/adminSlice';
import AuthDebug from '../debug/AuthDebug';
import { useTranslation } from 'react-i18next';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { admin, isAuthenticated } = useSelector(state => state.auth);
  const { admins } = useSelector(state => state.admin);

  useEffect(() => {
    // Fetch overview data
    dispatch(fetchAdmins());
  }, [dispatch]);

  const stats = [
    {
      title: t('dashboard.stats.totalAdmins'),
      value: admins.length,
      icon: (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
      ),
      bgColor: 'bg-blue-50',
      change: '+2.5%',
      changeType: 'positive'
    },
    {
      title: t('dashboard.stats.activeSessions'),
      value: 1,
      icon: (
        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      bgColor: 'bg-orange-50',
      change: '0%',
      changeType: 'neutral'
    }
  ];

  const recentActivities = [
    { type: 'login', message: t('dashboard.activities.login'), time: t('dashboard.time.justNow'), icon: '🔐' },
    { type: 'create', message: t('dashboard.activities.create'), time: t('dashboard.time.twoHoursAgo'), icon: '📄' },
    { type: 'update', message: t('dashboard.activities.update'), time: t('dashboard.time.oneDayAgo'), icon: '👤' },
    { type: 'system', message: t('dashboard.activities.system'), time: t('dashboard.time.twoDaysAgo'), icon: '💾' }
  ];

  return (
    <div className="space-y-6">
      {/* Debug Component - Remove after fixing auth issues */}
      {!isAuthenticated && <AuthDebug />}
      
      {/* Navigation Debug Panel */}
      {isAuthenticated && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">🔍 {t('dashboard.navDebug.title')}</h3>
          <div className="text-sm text-yellow-700">
            <p><strong>{t('dashboard.navDebug.currentUser')}</strong> {admin?.username || t('common.na')}</p>
            <p><strong>{t('dashboard.navDebug.testLinks')}</strong></p>
            <div className="mt-2 flex flex-wrap gap-2">
              <a href="/admins" className="bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">{t('navigation.admins')}</a>
              <a href="/events" className="bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">{t('navigation.events')}</a>
              <a href="/tithe-receipts" className="bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">{t('navigation.tithe-receipts')}</a>
              <a href="/attendance" className="bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200">{t('navigation.attendance')}</a>
            </div>
            <p className="mt-2 text-xs">{t('dashboard.navDebug.hint')}</p>
          </div>
        </div>
      )}
      
      {/* Security Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-green-800 text-sm font-medium">
            🔒 {t('dashboard.secureNotice')}
          </span>
        </div>
      </div>

      {/* Welcome Message */}
      <div className="bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          {t('dashboard.welcomeTitle', { name: admin?.full_name || admin?.username || t('common.na') })}
        </h1>
        <p className="text-indigo-100">
          {t('dashboard.welcomeSubtitle')}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">{stat.title}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'positive' ? 'text-green-600' : 
                    stat.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-gray-500 text-sm ml-1">{t('dashboard.vsLastMonth')}</span>
                </div>
              </div>
              <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                {stat.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.recentActivitiesTitle')}</h3>
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <span className="text-2xl">{activity.icon}</span>
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">{activity.message}</p>
                  <p className="text-gray-500 text-sm">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.systemStatus')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div>
              <p className="text-green-800 font-medium">{t('dashboard.status.backend')}</p>
              <p className="text-green-600 text-sm">{t('dashboard.status.operational')}</p>
            </div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div>
              <p className="text-green-800 font-medium">{t('dashboard.status.database')}</p>
              <p className="text-green-600 text-sm">{t('dashboard.status.connected')}</p>
            </div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div>
              <p className="text-green-800 font-medium">{t('dashboard.status.cms')}</p>
              <p className="text-green-600 text-sm">{t('dashboard.status.accessible')}</p>
            </div>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
